import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import { Package, User, Truck, RefreshCw, Filter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { fetchPackages, getAllLivreurs } from "../services/packagesService";
import { useUsersSearchQuery } from "@features/users/hooks/useUsers";
import PackageCardForLivreurManagement from "./PackageCardForLivreurManagement";
import HeadLayout from "@shared/layouts/HeadLayout";

// Package Status Filter Options
const PACKAGE_STATUS_FILTERS = [
  { value: "all", labelKey: "livreur.management.allStatuses" },
  { value: "order_received", labelKey: "livreur.management.orderReceived" },
  { value: "assigned", labelKey: "livreur.management.assigned_status" },
  { value: "picked_up", labelKey: "livreur.management.pickedUp" },
  { value: "in_transit", labelKey: "livreur.management.inTransit" },
  { value: "out_for_delivery", labelKey: "livreur.management.outForDelivery" },
  { value: "delivered", labelKey: "livreur.Delivered" },
  { value: "returned", labelKey: "livreur.management.returned" },
  { value: "cancelled", labelKey: "livreur.management.cancelled" },
];

// Client Selector Component
const ClientSelector = ({
  selectedClient,
  clientSearch,
  setClientSearch,
  clients,
  isLoadingClients,
  onClientSelect,
  onClientClear,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      {selectedClient ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-blue-50 dark:bg-blue-950/20">
          <User className="h-4 w-4 text-blue-600" />
          <Badge variant="secondary" className="flex items-center gap-1">
            {selectedClient.brandName || selectedClient.fullName}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClientClear}
              className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("livreur.management.searchClients")}
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setIsOpen(e.target.value.length >= 2);
            }}
            onFocus={() => setIsOpen(clientSearch.length >= 2)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          />
          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isLoadingClients ? (
                <div className="p-2 text-center">
                  <ThreeDotLoader />
                </div>
              ) : clients.length > 0 ? (
                clients.map((client) => (
                  <button
                    key={client._id}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    onClick={() => {
                      onClientSelect(client);
                      setIsOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {client.brandName || client.fullName}
                      </div>
                      {client.email && (
                        <div className="text-sm text-muted-foreground">
                          {client.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-2 text-center text-muted-foreground">
                  {t("livreur.management.noClientsFound")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Livreur Selector Component
const LivreurSelector = ({
  selectedLivreur,
  livreurSearch,
  setLivreurSearch,
  livreurs,
  isLoadingLivreurs,
  onLivreurSelect,
  onLivreurClear,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter livreurs based on search
  const filteredLivreurs = livreurs?.filter((livreur) => {
    const searchTerm = livreurSearch.toLowerCase();
    return (
      livreur.fullName?.toLowerCase().includes(searchTerm) ||
      livreur.email?.toLowerCase().includes(searchTerm) ||
      livreur.phone?.includes(searchTerm)
    );
  });

  return (
    <div className="relative">
      {selectedLivreur ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-green-50 dark:bg-green-950/20">
          <Truck className="h-4 w-4 text-green-600" />
          <Badge variant="secondary" className="flex items-center gap-1">
            {selectedLivreur.fullName || selectedLivreur.email}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLivreurClear}
              className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("Search livreurs...")}
            value={livreurSearch}
            onChange={(e) => {
              setLivreurSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          />
          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isLoadingLivreurs ? (
                <div className="p-2 text-center">
                  <ThreeDotLoader />
                </div>
              ) : filteredLivreurs && filteredLivreurs.length > 0 ? (
                filteredLivreurs.map((livreur) => (
                  <button
                    key={livreur._id}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    onClick={() => {
                      onLivreurSelect(livreur);
                      setIsOpen(false);
                    }}
                  >
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {livreur.fullName || livreur.email}
                      </div>
                      {livreur.phone && (
                        <div className="text-sm text-muted-foreground">
                          {livreur.phone}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-2 text-center text-muted-foreground">
                  {t("livreur.noLivreursFound")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function ImprovedAdminLivreurManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState({ limit: 20 });
  const [activeTab, setActiveTab] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [livreurSearch, setLivreurSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState(null);

  // Client search query
  const { data: clientsData, isLoading: isLoadingClients } =
    useUsersSearchQuery(clientSearch, 10, 1, clientSearch.length >= 2, {
      role: "client",
    });

  // Fetch livreurs
  const { data: livreursData, isLoading: isLoadingLivreurs } = useQuery({
    queryKey: ["admin-livreurs"],
    queryFn: getAllLivreurs,
  });

  const livreurs = livreursData?.livreurs || [];
  const clients = clientsData?.data || [];

  // Initialize filters from URL
  useEffect(() => {
    const status = searchParams.get("status");
    const search = searchParams.get("search");
    const assignment = searchParams.get("assignment");
    const client = searchParams.get("client");
    const livreur = searchParams.get("livreur");

    const urlFilters = {};
    if (status) urlFilters.status = status;
    if (search) urlFilters.search = search;
    if (assignment) urlFilters.assignment = assignment;
    if (client) urlFilters.client = client;
    if (livreur) urlFilters.livreur = livreur;

    if (Object.keys(urlFilters).length > 0) {
      setParams((prev) => ({ ...prev, ...urlFilters, page: 1 }));
    }
  }, [searchParams]);

  // Build query params
  const buildPackageParams = (pageParam = 1) => {
    const queryParams = new URLSearchParams({
      page: pageParam,
      limit: params.limit || 20,
      sort: "createdAt",
      order: "desc",
    });

    if (params.status && params.status !== "all") {
      queryParams.append("status", params.status);
    }

    if (params.search) {
      queryParams.append("search", params.search);
    }

    if (params.client) {
      queryParams.append("client", params.client);
    }

    if (params.livreur && params.livreur !== "all") {
      queryParams.append("livreur", params.livreur);
    }

    // Handle assignment filter
    if (params.assignment === "assigned") {
      queryParams.append("hasLivreur", "true");
    } else if (params.assignment === "unassigned") {
      queryParams.append("hasLivreur", "false");
    }

    return queryParams.toString();
  };

  // Fetch all packages with infinite scroll
  const {
    data: allPackagesData,
    fetchNextPage: fetchNextAllPage,
    hasNextPage: hasNextAllPage,
    isFetchingNextPage: isFetchingNextAllPage,
    isLoading: isLoadingAll,
    error: allError,
    refetch: refetchAll,
  } = useInfiniteQuery({
    queryKey: ["admin-packages-infinite", params],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPackages(buildPackageParams(pageParam));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "all",
  });

  // Fetch unassigned packages
  const {
    data: unassignedPackagesData,
    fetchNextPage: fetchNextUnassignedPage,
    hasNextPage: hasNextUnassignedPage,
    isFetchingNextPage: isFetchingNextUnassignedPage,
    isLoading: isLoadingUnassigned,
    error: unassignedError,
    refetch: refetchUnassigned,
  } = useInfiniteQuery({
    queryKey: ["admin-packages-unassigned", params],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams({
        page: pageParam,
        limit: params.limit || 20,
        sort: "createdAt",
        order: "desc",
        hasLivreur: "false",
      });

      if (params.status && params.status !== "all") {
        queryParams.append("status", params.status);
      }

      if (params.search) {
        queryParams.append("search", params.search);
      }

      if (params.client) {
        queryParams.append("client", params.client);
      }

      return fetchPackages(queryParams.toString());
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "unassigned",
  });

  const allPackages =
    allPackagesData?.pages?.flatMap((page) => page.data) || [];
  const totalAllPackages =
    allPackagesData?.pages?.[0]?.pageInfo?.totalCount || 0;

  const unassignedPackages =
    unassignedPackagesData?.pages?.flatMap((page) => page.data) || [];
  const totalUnassignedPackages =
    unassignedPackagesData?.pages?.[0]?.pageInfo?.totalCount || 0;

  // Calculate stats
  const getStatsForPackages = (packages) => {
    return {
      total: packages.length,
      assigned: packages.filter((p) => p.livreur).length,
      unassigned: packages.filter((p) => !p.livreur).length,
      delivered: packages.filter((p) => p.status === "signed").length,
      inTransit: packages.filter((p) =>
        ["picked_up", "in_transit", "out_for_delivery"].includes(p.status)
      ).length,
      pending: packages.filter((p) =>
        ["order_received", "assigned"].includes(p.status)
      ).length,
    };
  };

  const handleFilter = (key, value) => {
    const apiValue = value === "all" ? "" : value;
    const newParams = { ...params, [key]: apiValue, page: 1 };
    setParams(newParams);

    const newSearchParams = new URLSearchParams(searchParams);
    if (apiValue) {
      newSearchParams.set(key, apiValue);
    } else {
      newSearchParams.delete(key);
    }
    setSearchParams(newSearchParams);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch("");
    handleFilter("client", client._id);
  };

  const handleClientClear = () => {
    setSelectedClient(null);
    handleFilter("client", "");
  };

  const handleLivreurSelect = (livreur) => {
    setSelectedLivreur(livreur);
    setLivreurSearch("");
    handleFilter("livreur", livreur._id);
  };

  const handleLivreurClear = () => {
    setSelectedLivreur(null);
    handleFilter("livreur", "");
  };

  const handleRefresh = () => {
    if (activeTab === "all") {
      refetchAll();
    } else {
      refetchUnassigned();
    }
    toast.success(t("Data refreshed"));
  };

  const handleClearFilters = () => {
    setParams({ limit: 20 });
    setSearchParams(new URLSearchParams());
    setSelectedClient(null);
    setSelectedLivreur(null);
    setClientSearch("");
    setLivreurSearch("");
  };

  if (allError || unassignedError) {
    toast.error(t("Failed to load packages"));
  }

  const renderPackagesList = (
    packages,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  ) => (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <ThreeDotLoader />
        </div>
      ) : packages.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t("No packages found")}
            </h3>
            <p className="text-muted-foreground">
              {params.status || params.search || params.client || params.livreur
                ? t("No packages match your current filters")
                : t("No packages available yet")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {packages.map((pkg) => (
              <PackageCardForLivreurManagement
                key={pkg._id}
                packageData={pkg}
                onSuccess={handleRefresh}
              />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <InfiniteScrollTrigger
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <HeadLayout title={t("livreur.management.title")} />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("livreur.management.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("livreur.management.description")}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoadingAll || isLoadingUnassigned}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${
                isLoadingAll || isLoadingUnassigned ? "animate-spin" : ""
              }`}
            />
            {t("Refresh")}
          </Button>
        </div>

        {/* Main Content with Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">
              {t("livreur.management.allPackages")}
            </TabsTrigger>
            <TabsTrigger value="unassigned">
              {t("Unassigned")} ({totalUnassignedPackages})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {(() => {
                const stats = getStatsForPackages(allPackages);
                return (
                  <>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold">
                          {totalAllPackages}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.totalPackages")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">
                          {stats.assigned}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.assigned")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {stats.unassigned}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Unassigned")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {stats.delivered}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.delivered")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-yellow-600">
                          {stats.inTransit}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.inTransit")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-gray-600">
                          {stats.pending}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.pending")}
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Filters */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    {t("livreur.management.filterPackages")}
                  </CardTitle>
                  {(params.status ||
                    params.search ||
                    params.client ||
                    params.livreur) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      {t("livreur.management.clearFilters")}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Select
                    value={params.status || "all"}
                    onValueChange={(value) => handleFilter("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("filterByStatus")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PACKAGE_STATUS_FILTERS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ClientSelector
                    selectedClient={selectedClient}
                    clientSearch={clientSearch}
                    setClientSearch={setClientSearch}
                    clients={clients}
                    isLoadingClients={isLoadingClients}
                    onClientSelect={handleClientSelect}
                    onClientClear={handleClientClear}
                    t={t}
                  />

                  <LivreurSelector
                    selectedLivreur={selectedLivreur}
                    livreurSearch={livreurSearch}
                    setLivreurSearch={setLivreurSearch}
                    livreurs={livreurs}
                    isLoadingLivreurs={isLoadingLivreurs}
                    onLivreurSelect={handleLivreurSelect}
                    onLivreurClear={handleLivreurClear}
                    t={t}
                  />

                  <Input
                    placeholder={t("livreur.management.searchPackages")}
                    value={params.search || ""}
                    onChange={(e) => handleFilter("search", e.target.value)}
                    className="md:col-span-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Packages List */}
            {renderPackagesList(
              allPackages,
              isLoadingAll,
              hasNextAllPage,
              isFetchingNextAllPage,
              fetchNextAllPage
            )}
          </TabsContent>

          <TabsContent value="unassigned" className="space-y-6">
            {/* Stats Cards for Unassigned */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(() => {
                const stats = getStatsForPackages(unassignedPackages);
                return (
                  <>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {totalUnassignedPackages}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.unassignedPackages")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-gray-600">
                          {stats.pending}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.pending")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {livreurs.length}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.availableLivreurs")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-purple-600">
                          {stats.inTransit}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("livreur.management.inTransit")}
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Filters for Unassigned */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    {t("Filter Unassigned Packages")}
                  </CardTitle>
                  {(params.status || params.search || params.client) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      {t("Clear Filters")}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    value={params.status || "all"}
                    onValueChange={(value) => handleFilter("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("Filter by status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PACKAGE_STATUS_FILTERS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ClientSelector
                    selectedClient={selectedClient}
                    clientSearch={clientSearch}
                    setClientSearch={setClientSearch}
                    clients={clients}
                    isLoadingClients={isLoadingClients}
                    onClientSelect={handleClientSelect}
                    onClientClear={handleClientClear}
                    t={t}
                  />

                  <Input
                    placeholder={t("Search packages...")}
                    value={params.search || ""}
                    onChange={(e) => handleFilter("search", e.target.value)}
                    className="md:col-span-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Unassigned Packages List */}
            {renderPackagesList(
              unassignedPackages,
              isLoadingUnassigned,
              hasNextUnassignedPage,
              isFetchingNextUnassignedPage,
              fetchNextUnassignedPage
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export default ImprovedAdminLivreurManagement;
